import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Checkbox,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

export type TournamentPlayer = {
  placement: number;
  profileId: string;
  username: string;
};

type PropTypes = {
  close: () => void;
  open: boolean;
  roundResults?: Record<string, TournamentPlayer[]>;
  whoCanJoin: (selectedPlayers: TournamentPlayer[]) => void;
};

export const WhoCanJoinGameDialog = ({
  close,
  open,
  roundResults,
  whoCanJoin,
}: PropTypes) => {
  const [checked, setChecked] = useState<string[]>([]);
  const [round, setRound] = useState('cutOne');
  const [winners, setWinners] = useState<TournamentPlayer[]>([]);
  const [touchedPlayers, setTouchedPlayers] = useState<TournamentPlayer[]>([]);
  const [checkedAll, setCheckedAll] = useState(false);

  useEffect(() => {
    if (!open || !roundResults) return;
    setWinners(roundResults[round]);
  }, [open]);

  useEffect(() => {
    if (!roundResults) return;
    setWinners(roundResults[round]);
  }, [round]);

  useEffect(() => {
    if (checkedAll) {
      setChecked(winners.map((w) => w.profileId));
      setTouchedPlayers(winners);
    } else {
      setChecked([]);
      setTouchedPlayers([]);
    }
  }, [checkedAll]);

  const handleToggle = (value: TournamentPlayer) => () => {
    const currentIndex = checked.indexOf(value.profileId);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value.profileId);
      setTouchedPlayers((prev) => prev.concat(value));
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleSubmit = () => {
    const selectedPlayers = touchedPlayers.filter((p) =>
      checked.includes(p.profileId)
    );
    whoCanJoin(selectedPlayers);
    close();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md">
      <DialogTitle>
        <Stack spacing={2}>
          <Typography variant="h5" textAlign={'center'}>
            Select who can join
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '100%', height: '100%' }}>
        <Stack spacing={2}>
          <FormControl variant="standard" sx={{ minWidth: 100 }}>
            <InputLabel>Round Results</InputLabel>
            <Select value={round} onChange={(e) => setRound(e.target.value)}>
              <MenuItem value={'finals'}>Finals</MenuItem>
              <MenuItem value={'semiFinals'}>Semi Finals</MenuItem>
              <MenuItem value={'cutFour'}>Cut 4</MenuItem>
              <MenuItem value={'cutThree'}>Cut 3</MenuItem>
              <MenuItem value={'cutTwo'}>Cut 2</MenuItem>
              <MenuItem value={'cutOne'}>Cut 1</MenuItem>
              <MenuItem value={'participants'}>Participants</MenuItem>
            </Select>
          </FormControl>
          <Button onClick={() => setCheckedAll((prev) => !prev)}>
            Select/Unselect all
          </Button>
          <List
            sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
          >
            {winners.map((player) => {
              const labelId = `checkbox-list-label-${player.profileId}`;

              return (
                <ListItem key={player.profileId} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={handleToggle(player)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checked.indexOf(player.profileId) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={player.username} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={close} fullWidth>
          Cancel
        </Button>
        <Button variant="text" onClick={handleSubmit} fullWidth>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
